import { useEffect, useState } from "react";
import type { BacaClip } from "@jomcerdik/shared";
import { speak, speakEn } from "./speak";

/**
 * JomBaca Tadika playback, in this order:
 *  1. real audio for the clip (human recording, else pre-generated) — fetched once, kept as a blob;
 *  2. the device's own Malay/Indonesian voice (never English);
 *  3. silence.
 * Display text and sound are separate: the clip carries its own pronunciation (see tadikaAudio.ts).
 */
type Entry = { file: string; source: string };
let known: Record<string, Entry> | null = null;
let manifest: Promise<Record<string, Entry>> | null = null;
const blobs = new Map<string, string>(); // clip key → object URL
const loading = new Map<string, Promise<string | null>>();

export function loadManifest(): Promise<Record<string, Entry>> {
  // Only a successful answer is kept: a 401 before login (or a network blip) must not pin "no audio".
  manifest ??= fetch("/api/learn/audio/manifest", { credentials: "same-origin" })
    .then(async (r) => {
      if (!r.ok) throw new Error(String(r.status));
      const clips = ((await r.json()) as { clips?: Record<string, Entry> }).clips ?? {};
      known = clips;
      return clips;
    })
    .catch(() => { manifest = null; return {}; });
  return manifest;
}

const urlOf = (e: Entry) => `/api/learn/audio/f/${e.file}`;

function fetchBlob(key: string, e: Entry): Promise<string | null> {
  const have = blobs.get(key);
  if (have) return Promise.resolve(have);
  let p = loading.get(key);
  if (!p) {
    p = fetch(urlOf(e), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (!b) return null; const u = URL.createObjectURL(b); blobs.set(key, u); return u; })
      .catch(() => null)
      .finally(() => loading.delete(key));
    loading.set(key, p);
  }
  return p;
}

/** Download a page's clips in the background so a tap plays instantly (and inside the gesture on iOS). */
export async function prefetchClips(clips: BacaClip[]): Promise<void> {
  const m = await loadManifest();
  const todo = clips.filter((c) => m[c.key] && !blobs.has(c.key));
  for (let i = 0; i < todo.length; i += 6) await Promise.all(todo.slice(i, i + 6).map((c) => fetchBlob(c.key, m[c.key]!)));
}

let current: HTMLAudioElement | null = null;
let queueToken = 0;

function playUrl(url: string): HTMLAudioElement {
  current?.pause();
  const a = new Audio(url);
  current = a;
  void a.play().catch(() => undefined);
  return a;
}

/** Play one clip. `fallback` is what the device voice should read if there is no audio file. */
export function playClip(clip: BacaClip, fallback: string = clip.text.replace(/-/g, " "), rate = 0.75): void {
  queueToken++;
  const blob = blobs.get(clip.key);
  if (blob) { playUrl(blob); return; }
  const e = known?.[clip.key];
  if (e) { playUrl(urlOf(e)); void fetchBlob(clip.key, e); return; } // streams (Range) now, cached for next time
  current?.pause();
  if (clip.language === "en-GB") speakEn(fallback, rate + 0.1); else if (clip.kind !== "jawi") speak(fallback, rate);
}

/** Several clips one after another on the same element (stories). Stops when anything else plays. */
export function playQueue(clips: BacaClip[], fallback: string): void {
  const token = ++queueToken;
  const m = known ?? {};
  if (!clips.every((c) => blobs.has(c.key) || m[c.key])) { current?.pause(); if (clips[0]?.language === "en-GB") speakEn(fallback, 0.85); else if (!clips.some(c => c.kind === "jawi")) speak(fallback, 0.75); return; }
  current?.pause();
  const a = new Audio();
  current = a;
  let i = 0;
  const next = () => {
    if (token !== queueToken || i >= clips.length) return;
    const c = clips[i++]!;
    a.src = blobs.get(c.key) ?? urlOf(m[c.key]!);
    void a.play().catch(() => undefined);
  };
  a.addEventListener("ended", () => setTimeout(next, 350));
  next();
}

/** "audio" when real audio exists for these clips, "device" when only the phone voice will speak. */
export function useAudioSource(clips: BacaClip[]): "loading" | "audio" | "partial" | "device" {
  const [s, setS] = useState<"loading" | "audio" | "partial" | "device">("loading");
  const sig = clips.map((c) => c.key).join(",");
  useEffect(() => {
    let live = true;
    void loadManifest().then((m) => {
      const n = clips.filter((c) => m[c.key]).length;
      if (live) setS(n === 0 ? "device" : n === clips.length ? "audio" : "partial");
      void prefetchClips(clips);
    });
    return () => { live = false; };
  }, [sig]);
  return s;
}

